import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { NavigationMenu } from '@/components/molecules/NavigationMenu'
import { ROUTES } from '@/config/routes'
import { useNavigationStore } from '@/stores/navigation'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface SidePanelProps {
  className?: string
  isCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}

export const SidePanel: React.FC<SidePanelProps> = ({ 
  className = "",
  isCollapsed: isCollapsedProp,
  onCollapsedChange 
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { setNavigating } = useNavigationStore()
  
  // Use local state if not controlled
  const [localIsCollapsed, setLocalIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    return saved === 'true'
  })
  
  const isCollapsed = isCollapsedProp ?? localIsCollapsed
  
  const handleToggleCollapse = () => {
    const newValue = !isCollapsed
    if (onCollapsedChange) {
      onCollapsedChange(newValue)
    } else {
      setLocalIsCollapsed(newValue)
      localStorage.setItem('sidebar-collapsed', String(newValue))
    }
  }

  useEffect(() => {
    setNavigating(false)
  }, [location.pathname, setNavigating])

  const getActiveItem = () => {
    const pathname = location.pathname
    
    // Check for exact matches first
    switch (pathname) {
      case ROUTES.DASHBOARD:
        return 'dashboard'
      case ROUTES.TEAMS:
        return 'teams'
      case ROUTES.TEMPLATES:
        return 'templates'
      case ROUTES.CAMPAIGNS:
        return 'campaigns'
      case ROUTES.AGENTS:
        return 'agents'
      case ROUTES.CHANNELS:
        return 'channels'
      case ROUTES.LIBRARY:
        return 'library'
      case ROUTES.BILLING:
        return 'billing'
      case ROUTES.CONTACTS:
        return 'contacts'
      case ROUTES.INBOX:
        return 'inbox'
      case ROUTES.ANALYTICS:
        return 'analytics'
    }
    
    // Check for pattern matches (dynamic routes)
    if (pathname.startsWith('/channels/')) {
      return 'channels'
    }
    if (pathname.startsWith('/campaigns/')) {
      return 'campaigns'
    }
    if (pathname.startsWith('/agents/')) {
      return 'agents'
    }
    if (pathname.startsWith('/templates/')) {
      return 'templates'
    }
    
    // Default fallback
    return 'dashboard'
  }

  const handleItemClick = (item: string) => {
    let targetRoute = ''
    
    switch (item) {
      case 'dashboard':
        targetRoute = ROUTES.DASHBOARD
        break
      case 'teams':
        targetRoute = ROUTES.TEAMS
        break
      case 'templates':
        targetRoute = ROUTES.TEMPLATES
        break
      case 'campaigns':
        targetRoute = ROUTES.CAMPAIGNS
        break
      case 'agents':
        targetRoute = ROUTES.AGENTS
        break
      case 'channels':
        targetRoute = ROUTES.CHANNELS
        break
      case 'library':
        targetRoute = ROUTES.LIBRARY
        break
      case 'billing':
        targetRoute = ROUTES.BILLING
        break
      case 'contacts':
        targetRoute = ROUTES.CONTACTS
        break
      case 'inbox':
        targetRoute = ROUTES.INBOX
        break
      case 'analytics':
        targetRoute = ROUTES.ANALYTICS
        break
      default:
        return
    }
    
    if (targetRoute && targetRoute !== location.pathname) {
      setNavigating(true, targetRoute)
      setTimeout(() => {
        navigate(targetRoute)
      }, 100)
    }
  }

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-52'} bg-card border-r border-border shadow-sm transition-all duration-300 hover:shadow-md flex-shrink-0 relative flex flex-col ${className}`}>
      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggleCollapse}
        className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full bg-card border border-border shadow-md hover:shadow-lg hover:scale-110 transition-all duration-200"
      >
        {isCollapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </Button>
      
      <div className="flex-1 overflow-y-auto w-full">
        <div className={`${isCollapsed ? 'px-2 py-2' : 'px-3 py-3'} w-full`}>
          <NavigationMenu
            activeItem={getActiveItem()}
            onItemClick={handleItemClick}
            isCollapsed={isCollapsed}
          />
        </div>
      </div>
    </div>
  )
}